import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsButton,
  KdsButtonIcon,
  KdsText,
  KdsTextField,
  VStack,
} from "@kazaar-platform/design-system";
import { RiCloseLine, RiDeleteBinLine, RiEditLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { useOrderItems, useToasts } from "@/hooks";
import {
  ClientReferenceBlocSchema,
  type InferredClientReferenceBlocSchema,
} from "./ClientReferenceBloc.schema";
import { type ClientReferenceBlocProps } from "./ClientReferenceBloc.types";

export const ClientReferenceBloc = ({
  orderItem,
}: ClientReferenceBlocProps) => {
  // States
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Hooks
  const { orderId } = useParams();
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useUpdateOrderItem } = useOrderItems();
  const { mutateAsync: updateOrderItem } = useUpdateOrderItem();

  // Form
  const methods = useForm<InferredClientReferenceBlocSchema>({
    resolver: zodResolver(ClientReferenceBlocSchema),
    defaultValues: {
      clientReference: orderItem?.clientReference || "",
    },
  });

  // Check if a modification request is pending
  const hasAModificationRequest = orderItem.Workflow.PostponedActivities.some(
    (postponedActivity) =>
      postponedActivity.activity ===
      WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest
  );

  // Functions
  const handleClientReferenceSubmit = async (
    data: InferredClientReferenceBlocSchema
  ) => {
    if (data.clientReference === orderItem?.clientReference) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);

    try {
      await updateOrderItem({
        orderId: orderId as string,
        orderItemId: orderItem.id,
        orderItemUpdateCommand: {
          clientReference: data.clientReference ?? "",
          allowEditionOrderItem: orderItem?.order.status === "IN_PROGRESS",
        },
      });

      add({
        state: "success",
        title: t("orderItemSummary.referenceId.toast.success"),
      });
      setIsEditing(false);
    } catch (_error) {
      add({
        state: "error",
        title: t("orderItemSummary.referenceId.toast.error"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) =>
        void methods.handleSubmit(handleClientReferenceSubmit)(e)
      }
    >
      <Box rounded="br-lg" bgColor="elevation.first" p="sp-md">
        <VStack gap="sp-xs">
          <KdsText as="h2" size="title-sm">
            {t("orderItemSummary.referenceId.title")}
          </KdsText>

          <KdsText color="text-bodyvariant">
            {t("orderItemSummary.referenceId.description")}
          </KdsText>

          <HStack gap="sp-xs" align="center">
            {!isEditing && (
              <>
                <KdsText
                  size="subtitle-md"
                  color={
                    orderItem?.clientReference
                      ? "text-body"
                      : "text-bodyvariant"
                  }
                  isItalic={!orderItem?.clientReference}
                >
                  {orderItem?.clientReference ||
                    t("orderItemSummary.referenceId.noReference")}
                </KdsText>

                {orderItem.status === "IN_PROGRESS" &&
                  !hasAModificationRequest && (
                    <>
                      <KdsButtonIcon
                        variant={
                          orderItem?.clientReference ? "outlined" : "contained"
                        }
                        onPress={() => setIsEditing(!isEditing)}
                        icon={RiEditLine}
                        size="small"
                      />

                      {orderItem?.clientReference && (
                        <KdsButtonIcon
                          onPress={() => {
                            void handleClientReferenceSubmit({
                              clientReference: "",
                            });
                            methods.setValue("clientReference", "");
                          }}
                          icon={RiDeleteBinLine}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </>
                  )}
              </>
            )}

            {isEditing && (
              <>
                <Controller
                  {...methods}
                  name="clientReference"
                  render={({ field }) => (
                    <KdsTextField
                      iconRight={RiCloseLine}
                      placeholder={t(
                        "orderItemSummary.referenceId.inputPlaceholder"
                      )}
                      onRightIconPress={() =>
                        methods.setValue("clientReference", "")
                      }
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                <KdsButton type="submit" isLoading={isSaving}>
                  {t("buttons.record")}
                </KdsButton>

                {orderItem?.clientReference && (
                  <KdsButtonIcon
                    onPress={() => {
                      void handleClientReferenceSubmit({ clientReference: "" });
                      methods.setValue("clientReference", "");
                    }}
                    icon={RiDeleteBinLine}
                    color="error"
                    variant="outlined"
                  />
                )}
              </>
            )}
          </HStack>
        </VStack>
      </Box>
    </form>
  );
};
